import { useLocation } from "@tanstack/react-router";
import { useEffect } from "react";

import type { CommentsNavigationState } from "@/react/components/CommentsCompass/types";
import {
  useIsPostModalActive,
  useReplaceLocationState,
} from "@/react/hooks/usePostModalNavigation";
import { useIsPostModalOverlay } from "@/react/hooks/usePostModalParams";

export const scrollToCommentsOnMount = (): CommentsNavigationState => ({
  shouldFocusOnCommentsSection: true,
});

export const useConsumeScrollToCommentsOnMount = (): boolean => {
  const replaceLocationState = useReplaceLocationState();
  const isPostModalActive = useIsPostModalActive();
  const isPostModalOverlay = useIsPostModalOverlay();

  const shouldFocusOnCommentsSection = useLocation({
    select: location =>
      (location.state as CommentsNavigationState | undefined)
        ?.shouldFocusOnCommentsSection ?? false,
  });

  // Navigation state is global — when a post modal is open, the background
  // event/post page shares the same state. Scroll to comments only inside the
  // modal and ignore the background page; full-page scroll runs only when no
  // modal is open.
  const shouldScrollToComments =
    shouldFocusOnCommentsSection && (isPostModalOverlay || !isPostModalActive);

  useEffect(() => {
    if (!shouldScrollToComments) return;
    replaceLocationState({ shouldFocusOnCommentsSection: undefined });
  }, [shouldScrollToComments, replaceLocationState]);

  return shouldScrollToComments;
};
